import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/common/PageHeader';
import { useAdminHealth } from '../../api/generated/generated';

function statusColor(status: string): 'success' | 'error' | 'default' {
  if (status === 'ok') return 'success';
  if (status === 'error') return 'error';
  return 'default';
}

export function AdminHealthPage() {
  const { user } = useAuth();
  const { data: healthResp, isLoading, error } = useAdminHealth({
    query: { refetchInterval: 30_000 },
  });
  const data = healthResp?.data;

  if (!user?.superAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        You do not have permission to access this page.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load system health.
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title="System Health"
        actions={
          <Chip
            label={data.status}
            color={data.status === 'healthy' ? 'success' : 'warning'}
          />
        }
      />
      <Typography variant="h6" gutterBottom>
        Services
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.entries(data.services).map(([name, status]) => (
          <Grid key={name} size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  {name.replace('_', ' ').toUpperCase()}
                </Typography>
                <Chip label={status} color={statusColor(status)} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography variant="h6" gutterBottom>
        Platform Stats
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3">{data.stats.total_organizations}</Typography>
              <Typography sx={{
                color: "text.secondary"
              }}>Organizations</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3">{data.stats.total_users}</Typography>
              <Typography sx={{
                color: "text.secondary"
              }}>Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3">{data.stats.total_conversations}</Typography>
              <Typography sx={{
                color: "text.secondary"
              }}>Conversations</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mt: 2
        }}>
        Last updated: {new Date(data.timestamp).toLocaleString()} (auto-refreshes every 30s)
      </Typography>
    </Box>
  );
}
